import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Spacer,
  Box,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Card,
  CardBody,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { ReactElement, useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  ChartTimePeriod,
  cohortCadenceLabelFormatter,
  CohortConfig,
  defaultCohortCadence,
  defaultMaxSeriesAge,
  formatCohortCadenceSelectOptions,
  onToggleActiveField,
  PortfolioChartConfig,
  ProfitsVintageInsight,
  ProfitsVintageTableRow,
} from '../shared/portfolioChartUtils';
import { PortfolioVintageChartCards } from '../shared/PortfolioVintageChartCards';
import { PortfolioVintageReChart } from '../shared/PortfolioVintageReChart';
import { ProfitLossVintageTable } from './ProfitLossVintageTable';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Select } from '~/packages/common-frontend/components/Select';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { PortfolioDownloadCsvLink } from '~/packages/portfolio/ui/shared/PortfolioDownloadCsvLink';

type VintageChartTemplateProps = {
  setSelectedMaxSeriesAge: React.Dispatch<React.SetStateAction<number>>;
  tableDataByOriginationDate: ProfitsVintageTableRow[];
  selectedCohortConfig: CohortConfig;
  setSelectedCohortConfig: React.Dispatch<React.SetStateAction<CohortConfig>>;
  chartTitle: string;
  activeFields: Partial<keyof ProfitsVintageInsight>[];
  setActiveFields: React.Dispatch<
    React.SetStateAction<Partial<keyof ProfitsVintageInsight>[]>
  >;
  selectedDatapoint: ProfitsVintageInsight | null;
  setSelectedDatapoint: React.Dispatch<
    React.SetStateAction<ProfitsVintageInsight | null>
  >;
  yAxisLabel?: string;
  chartConfig: PortfolioChartConfig<string>;
  chartDataWithCohortCadence: ProfitsVintageInsight[];
  chartDataDateRange: {
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  };
  // eslint-disable-next-line react/no-unused-prop-types
  setChartDataDateRange: React.Dispatch<
    React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>
  >;
  fieldType: CreditAgreement.DataTapeField.Type;
  onSelectDateRangeHandler: (newDateRange: [Date | null, Date | null]) => void;
  showOriginatedReceivablesCount: boolean;
  chartTypeSelector?: ReactElement;
};
export const VintageChartTemplate = ({
  setSelectedMaxSeriesAge,
  tableDataByOriginationDate,
  selectedCohortConfig,
  setSelectedCohortConfig,
  chartTitle,
  activeFields,
  setActiveFields,
  selectedDatapoint,
  yAxisLabel,
  chartConfig,
  setSelectedDatapoint,
  chartDataWithCohortCadence,
  chartDataDateRange,
  fieldType,
  onSelectDateRangeHandler,
  showOriginatedReceivablesCount,
  chartTypeSelector,
}: VintageChartTemplateProps) => {
  const doesDataForChartExist = chartDataWithCohortCadence.some(
    (cohortSeries) => {
      return Object.entries(cohortSeries).some(([key, value]) => {
        return key !== 'cohortSeriesAge' && value !== null;
      });
    },
  );

  const cohortCadenceSelectOptions = useMemo(
    () => formatCohortCadenceSelectOptions(),
    [],
  );

  const downloadFileHeaders =
    tableDataByOriginationDate.length > 0
      ? _.map(_.keys(tableDataByOriginationDate[0]), (headerKey) => {
          return {
            label: commonUtils.toTitleCase(headerKey),
            key: headerKey,
          };
        })
      : undefined;

  const formattedDownloadData = tableDataByOriginationDate.map((row) => {
    const newRow = { ...row };
    newRow.originationDate = cohortCadenceLabelFormatter(
      selectedCohortConfig.cohortBaseCadence,
      newRow.originationDate,
    ) as Util.FullDateStr;
    return newRow;
  });

  return (
    <Card variant="outline" size="lg">
      <CardBody>
        <Flex>
          <Box width={doesDataForChartExist ? '70%' : '100%'}>
            <Flex justifyContent="space-between" alignItems="center">
              <Typography variant="section-subtitle">{chartTitle}</Typography>
              {chartTypeSelector}
            </Flex>
            <SimpleGrid columns={2} spacing="24px" mb="40px">
              <Card variant="outline">
                <CardBody display="flex" flexDirection="column" gap="1rem">
                  <Typography fontWeight="semibold">Vintage</Typography>
                  <Select
                    value={selectedCohortConfig.cohortBaseCadence}
                    onChange={(cadence) =>
                      setSelectedCohortConfig({
                        ...selectedCohortConfig,
                        cohortBaseCadence: cadence ?? defaultCohortCadence,
                      })
                    }
                    options={cohortCadenceSelectOptions}
                  />
                  <FullDateRangePicker
                    onSelectDateRange={onSelectDateRangeHandler}
                    currentDateRange={{
                      startDate: chartDataDateRange.chartDataStartDate,
                      endDate: chartDataDateRange.chartDataEndDate,
                    }}
                    width="100%"
                  />
                </CardBody>
              </Card>
              <Card variant="outline">
                <CardBody display="flex" flexDirection="column" gap="1rem">
                  <Typography fontWeight="semibold">Timeline</Typography>
                  {/* positive integers only */}
                  <NumberInput
                    step={1}
                    defaultValue={12}
                    min={0}
                    precision={0}
                    onChange={(value) => {
                      setSelectedMaxSeriesAge(_.toNumber(value));
                    }}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper>
                        <Icon icon="caret-up" type="solid" />
                      </NumberIncrementStepper>
                      <NumberDecrementStepper>
                        <Icon icon="caret-down" type="solid" />
                      </NumberDecrementStepper>
                    </NumberInputStepper>
                  </NumberInput>
                  <Select
                    value={selectedCohortConfig.cohortSeriesCadence}
                    onChange={(cadence) =>
                      setSelectedCohortConfig({
                        ...selectedCohortConfig,
                        cohortSeriesCadence: cadence ?? defaultCohortCadence,
                      })
                    }
                    options={cohortCadenceSelectOptions}
                  />
                </CardBody>
              </Card>
            </SimpleGrid>
            {!doesDataForChartExist && (
              <Alert status="warning" borderRadius="6px">
                <AlertIcon />
                <AlertDescription fontWeight="semibold">
                  Data is not available for the selected date range. Select
                  another date range.
                </AlertDescription>
              </Alert>
            )}
            {doesDataForChartExist && (
              <PortfolioVintageReChart
                chartData={chartDataWithCohortCadence}
                chartTitle={chartTitle}
                selectedCohortSeriesCadence={
                  selectedCohortConfig.cohortSeriesCadence
                }
                currentSelectedCohortSeriesAge={
                  selectedDatapoint
                    ? selectedDatapoint.cohortSeriesAge
                    : defaultMaxSeriesAge
                }
                onSelectDatapoint={setSelectedDatapoint}
                chartConfig={chartConfig}
                visibleChartComponents={activeFields}
                yAxisLabel={yAxisLabel}
                showLegend={false}
              />
            )}
          </Box>
          <Spacer />
          {selectedDatapoint && (
            <Flex width="25%" flexDirection="column">
              <PortfolioVintageChartCards
                selectedDatapoint={selectedDatapoint}
                chartConfig={chartConfig}
                cohortSeriesCadence={selectedCohortConfig.cohortSeriesCadence}
                activeFields={activeFields}
                onToggleActiveField={(selectedField) =>
                  onToggleActiveField<ProfitsVintageInsight>({
                    selectedField,
                    currentActiveFields: activeFields,
                    setActiveFields,
                  })
                }
              />

              {downloadFileHeaders && (
                <Flex justifyContent="flex-end">
                  <PortfolioDownloadCsvLink
                    dataRows={formattedDownloadData}
                    headers={downloadFileHeaders}
                    chartTitle={chartTitle}
                    selectedTimePeriod={
                      (selectedCohortConfig.cohortBaseCadence as unknown) as ChartTimePeriod
                    }
                  />
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
        {doesDataForChartExist && (
          <ProfitLossVintageTable
            tableData={tableDataByOriginationDate}
            selectedCohortConfig={selectedCohortConfig}
            maxSeriesAge={chartDataWithCohortCadence.length}
            fieldType={fieldType}
            showOriginatedReceivablesCount={showOriginatedReceivablesCount}
          />
        )}
      </CardBody>
    </Card>
  );
};
